import {
  EventLogger,
  landingTheme,
  LoggingEvent,
  searchBrowserAndVersion,
  useSnackbarStyles,
} from '@clef/client-library';
import { createTheme, CssBaseline, MuiThemeProvider } from '@material-ui/core';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import * as Sentry from '@sentry/react';
import { Contexts } from '@sentry/types';
import { SnackbarProvider } from 'notistack';
import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import BrowserDetectionSnackbar from './components/BrowserDetectionSnackbar/browserDetectionSnackbar';
import AppRouter from './components/Layout/AppRouter';
import GoogleSSOLoginRoute from './components/Layout/GoogleSSOLoginRoute';
import LdapLoginRoute from './components/Layout/LdapLoginRoute';
import { LoginRouter, SignupRouter } from './components/Layout/routes/AuthRoutes';
import SnackbarCloseButton from './components/SnackbarCloseButton';
import { EnvironmentEnum, getEnv } from './constants';
import CLEF_PATH from './constants/path';
import { useTypedSelector } from './hooks/useTypedSelector';
import ErrorPage from './pages/error/ErrorPage';
import Login from './pages/login/LoginContainer';
import ZeroAuthInstantLearning from './pages/ZeroAuthInstantLearning';
import {
  fetchEnableFeaturesDefault,
  resetEnabledFeaturesForUser,
} from './store/feature_toggle_store';
import { refreshAuthenticatedStates, setAuthenticated } from './store/newLoginState/actions';
import { UsersnapApiContextProvider } from './store/usersnapContext';
import Usersnap from './Usersnap';
import { SnackbarUtilsConfigurator } from './utils/snackbar_singleton';

import { queryClient } from '@/serverStore';
import { isCypressEnv } from '@clef/client-library/src/utils/env';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { DevTools as JotaiDevTools } from 'jotai-devtools';
import { AppLoadingSplash } from './components/Layout/components/Splash';
import { ClientFeatures, useFeatureGateEnabled } from './hooks/useFeatureGate';
import { getPlanName } from './pages/plan/utils';

const theme = createTheme(landingTheme);
const env = getEnv();
const isOnprem = env === EnvironmentEnum.Onprem;
const isProduction = env === EnvironmentEnum.Production;

// Create and Install Session Replay Plugin
const sessionReplayTracking = sessionReplayPlugin({
  sampleRate: 0.1,
});

amplitude.add(sessionReplayTracking);
amplitude.add(engagementPlugin());

type AuthenticationRouteType = {
  shouldBeAuthenticated: boolean;
  isAuthenticated: boolean;
  path: string;
};

const AuthenticationRoute: React.FC<AuthenticationRouteType> = ({
  path,
  children,
  shouldBeAuthenticated,
  isAuthenticated,
}) => {
  return (
    <Route
      path={path}
      render={prop =>
        shouldBeAuthenticated === isAuthenticated ? (
          children
        ) : (
          <Redirect to={{ ...prop.location, pathname: '/' }} />
        )
      }
    />
  );
};

const apiTimingLoggingFunc = () => {
  const performanceEntries = performance.getEntriesByType(
    'resource',
  ) as PerformanceResourceTiming[];
  performance.clearResourceTimings();
  const newEntries = performanceEntries
    .filter(_ => _.initiatorType === 'fetch')
    .map(_ => ({
      url: _.name,
      apiResponseTime: _.responseEnd - _.startTime,
      apiNetworkTime: _.responseEnd - _.requestStart,
    }));
  newEntries.forEach(entry => {
    EventLogger.log(LoggingEvent.PerformanceEntryEvent, entry);
  });
};

const App: React.FC = () => {
  const snackbarStyles = useSnackbarStyles();
  const { isOrgUserAuthenticated, isAuthenticated } = useTypedSelector(state => state.login);
  const currentUser = useTypedSelector(state => state.login.user);

  const isFinalAuthenticated = useMemo(
    () => isOrgUserAuthenticated || isAuthenticated,
    [isAuthenticated, isOrgUserAuthenticated],
  );

  const skipLogin = useFeatureGateEnabled(ClientFeatures.SkipAuthenticationFlows);

  const dispatch = useDispatch();

  const isAmplitudeInitialized = useRef(false);

  // Fetch default enabled feature
  useEffect(() => {
    dispatch(fetchEnableFeaturesDefault());
  }, [dispatch]);

  useEffect(() => {
    const apiTimingLoggingInterval = setInterval(apiTimingLoggingFunc, 10000);
    performance.onresourcetimingbufferfull = () => {
      apiTimingLoggingFunc(); // log and clear
    };
    window.addEventListener('beforeunload', () => {
      apiTimingLoggingFunc();
    });
    EventLogger.setMethods(
      () => {},
      (errorOrMessage, attributes, scopeFingerPrints) => {
        Sentry.withScope(function (scope) {
          if (scopeFingerPrints && scopeFingerPrints.length) {
            scope.setFingerprint(scopeFingerPrints);
          }
          Sentry.captureException(errorOrMessage, {
            contexts: attributes as Contexts,
          });
        });
      },
    );

    return () => {
      clearInterval(apiTimingLoggingInterval);
    };
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        dispatch(refreshAuthenticatedStates());

        // If org user is logged out, reset the features info for the user
        if (!localStorage.getItem('clef_is_login')) {
          dispatch(resetEnabledFeaturesForUser());
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch]);

  // Initialize Amplitude once without user ID to avoid anonymous users from landing page
  useEffect(() => {
    if (
      isProduction &&
      !isAmplitudeInitialized.current &&
      isFinalAuthenticated &&
      currentUser?.email
    ) {
      amplitude.init('2c3b78d4ccca7c17b23fad72e0cfd89e', currentUser.email, {
        defaultTracking: {
          sessions: true,
        },
        autocapture: {
          sessions: true,
          pageViews: true,
          attribution: true,
          fileDownloads: true,
          formInteractions: true,
          elementInteractions: true,
        },
      });
      // Set user properties
      const userIdentify = new amplitude.Identify();
      userIdentify.set('Account Name', `${currentUser.name} ${currentUser.lastName}`);
      userIdentify.set('User Role', currentUser.userRole);
      amplitude.identify(userIdentify);
      isAmplitudeInitialized.current = true;
    }
  }, [
    isFinalAuthenticated,
    currentUser?.email,
    currentUser?.name,
    currentUser?.userRole,
    currentUser?.lastName,
  ]);

  // Handle user identification and group properties when authentication state changes
  useEffect(() => {
    if (
      isProduction &&
      currentUser?.company &&
      currentUser?.orgId &&
      isAmplitudeInitialized.current
    ) {
      // Set user to group
      amplitude.setGroup('Organization', currentUser.company);

      // Set group properties with org information
      const currentSubscription = currentUser.subscriptions?.[0];
      const groupIdentify = new amplitude.Identify();
      groupIdentify.set('Organization Id', currentUser?.orgId);
      groupIdentify.set('Plan Type', getPlanName(currentSubscription));
      amplitude.groupIdentify('Organization', currentUser.company, groupIdentify);
    }
  }, [currentUser?.orgId, currentUser?.company, currentUser?.subscriptions]);

  return (
    <Suspense fallback={null}>
      <Sentry.ErrorBoundary
        onError={(e, componentStack) => {
          EventLogger.error(e, { errorInfo: e, componentStack });
        }}
      >
        <QueryClientProvider client={queryClient}>
          <MuiThemeProvider theme={theme}>
            <CssBaseline />
            <SnackbarProvider
              preventDuplicate
              maxSnack={3}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              classes={{
                root: snackbarStyles.root,
              }}
              action={snackbarKey => <SnackbarCloseButton snackbarKey={snackbarKey} />}
            >
              <SnackbarUtilsConfigurator />
              <BrowserDetectionSnackbar message={searchBrowserAndVersion().toString()} />
              <BrowserRouter>
                <Switch>
                  {/* / route is source of truth here */}
                  <Route
                    exact
                    path="/"
                    render={prop => (
                      <Redirect
                        to={{
                          ...prop.location,
                          pathname: isFinalAuthenticated ? '/app' : '/login',
                        }}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/ldap_aes_login"
                    render={prop => {
                      const url = window.location.href;
                      const index = url.indexOf('?');
                      if (isOnprem && index != -1) {
                        // this is for LDAP onprem
                        return (
                          <LdapLoginRoute
                            routeProps={prop}
                            encryptedLoginInfo={url.substring(index + 1)}
                          />
                        );
                      }
                      return <Redirect to={{ ...prop.location, pathname: '/' }} />;
                    }}
                  />
                  <Route
                    exact
                    path="/sso"
                    render={prop => {
                      localStorage.setItem('clef_account_is_login', '1');
                      localStorage.setItem('clef_is_login', '1');
                      dispatch(setAuthenticated(true));
                      return <Redirect to={{ ...prop.location, pathname: '/' }} />;
                    }}
                  />
                  <Route
                    exact
                    path="/login/google_sso"
                    render={() => {
                      return <GoogleSSOLoginRoute />;
                    }}
                  />
                  {/* Zero auth instant learning */}
                  <Route
                    exact
                    path={CLEF_PATH.zeroAuthInstantLearning}
                    render={() => <ZeroAuthInstantLearning />}
                  ></Route>
                  {/* Sign up pages */}
                  <AuthenticationRoute
                    path="/signup"
                    isAuthenticated={isFinalAuthenticated}
                    shouldBeAuthenticated={false}
                  >
                    <SignupRouter />
                  </AuthenticationRoute>
                  {/* Log in pages */}
                  <AuthenticationRoute
                    path={'/login'}
                    isAuthenticated={isFinalAuthenticated}
                    shouldBeAuthenticated={false}
                  >
                    {(skipLogin === true && <Redirect to="/sso" />) ||
                      (skipLogin === false && <LoginRouter />) ||
                      // when skipLogin is not ready from featureGate
                      // we should rather render loading state until it's ready
                      (skipLogin === undefined && <AppLoadingSplash />)}
                  </AuthenticationRoute>
                  {/* Legacy log in pages */}
                  <AuthenticationRoute
                    path="/legacy_login"
                    isAuthenticated={isFinalAuthenticated}
                    shouldBeAuthenticated={false}
                  >
                    <Login />
                  </AuthenticationRoute>
                  {/* Logged in pages */}
                  <AuthenticationRoute
                    path="/app"
                    isAuthenticated={isFinalAuthenticated}
                    shouldBeAuthenticated
                  >
                    <AppRouter />
                  </AuthenticationRoute>
                  {/* fallback to 404 pages */}
                  <Route render={() => <ErrorPage />} />
                </Switch>
              </BrowserRouter>
            </SnackbarProvider>
            <UsersnapApiContextProvider>
              <Usersnap />
            </UsersnapApiContextProvider>
            {!isCypressEnv() && <JotaiDevTools />}
            {!isCypressEnv() && <ReactQueryDevtools initialIsOpen={false} />}
          </MuiThemeProvider>
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </Suspense>
  );
};

export default App;
